import { Button } from '../ui/button';

/**
 * Multi-select chip-filter panel. Renders three independent chip rows
 * (City / Group / Organization) and a single "Favorites Only" toggle.
 *
 * All filter state lives in `SearchPage`; this is a pure presentational
 * wrapper so we don't duplicate the OR-within-type / AND-across-types
 * logic in two places.
 */
export default function SearchFiltersBar({
  allCities,
  selectedCities,
  setSelectedCities,
  groups,
  selectedGroups,
  setSelectedGroups,
  allOrgs,
  selectedOrgs,
  setSelectedOrgs,
  favOnly,
  setFavOnly,
  onClear,
}) {
  // Tiny inline helper — the parent doesn't need to expose its own
  // version since the only consumer is right here.
  const toggleArrayValue = (setter, value) =>
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  return (
    <div className="bg-card rounded-xl p-4 shadow-sm border border-border space-y-3 animate-slide-up" data-testid="search-filters">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-sm">Filters</h3>
        <button onClick={onClear} className="text-xs text-nav-blue hover:underline" data-testid="clear-filters-btn">
          Clear all
        </button>
      </div>

      <ChipRow
        label="City"
        items={allCities}
        selected={selectedCities}
        onToggle={(v) => toggleArrayValue(setSelectedCities, v)}
        keyFor={(v) => v}
        labelFor={(v) => v}
        testIdPrefix="filter-city-"
      />

      <ChipRow
        label="Group"
        items={groups}
        selected={selectedGroups}
        onToggle={(g) => toggleArrayValue(setSelectedGroups, g.id)}
        keyFor={(g) => g.id}
        labelFor={(g) => g.name}
        isSelected={(g) => selectedGroups.includes(g.id)}
        testIdPrefix="filter-group-"
        testIdFromItem={(g) => g.id}
      />

      <ChipRow
        label="Organization"
        items={allOrgs}
        selected={selectedOrgs}
        onToggle={(v) => toggleArrayValue(setSelectedOrgs, v)}
        keyFor={(v) => v}
        labelFor={(v) => v}
        testIdPrefix="filter-org-"
      />

      <Button
        variant={favOnly ? 'default' : 'outline'}
        onClick={() => setFavOnly(!favOnly)}
        className={`rounded-full text-xs ${favOnly ? 'bg-signal-yellow text-nav-blue hover:bg-signal-yellow/80' : ''}`}
        data-testid="filter-fav-btn"
      >
        {favOnly ? 'Favorites Only' : 'Show Favorites Only'}
      </Button>
    </div>
  );
}

/**
 * Internal helper — uniform chip-row UI used for City, Group, Org.
 * Renders nothing when `items` is empty (keeps the filters panel tight).
 */
function ChipRow({
  label, items, selected, onToggle,
  keyFor, labelFor, isSelected, testIdPrefix, testIdFromItem,
}) {
  if (!items?.length) return null;
  const _isSelected = isSelected ?? ((v) => selected.includes(v));
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 block">
        {label}
      </label>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => {
          const k = keyFor(it);
          const isActive = _isSelected(it);
          return (
            <button
              key={k}
              onClick={() => onToggle(it)}
              className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                isActive
                  ? 'bg-nav-blue text-white'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
              data-testid={`${testIdPrefix}${testIdFromItem ? testIdFromItem(it) : k}`}
            >
              {labelFor(it)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
